import { useState, useCallback, useRef, useEffect } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { haalSpeechToken, vertaal } from '@/lib/ai';

/**
 * Build an Azure PushAudioInputStream from any MediaStream.
 * Resamples to mono 16kHz 16-bit PCM (Azure's expected format).
 */
function buildPushStreamFromMediaStream(stream: MediaStream): {
  pushStream: SpeechSDK.PushAudioInputStream;
  cleanup: () => void;
} {
  const format = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(16000, 16, 1);
  const pushStream = SpeechSDK.AudioInputStream.createPushStream(format);

  const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AudioCtx();
  const inputSampleRate = ctx.sampleRate;
  const targetSampleRate = 16000;
  const ratio = inputSampleRate / targetSampleRate;

  const source = ctx.createMediaStreamSource(stream);
  // ScriptProcessorNode is deprecated but still the most reliable cross-browser way
  // to grab raw PCM samples without writing/loading an AudioWorklet module.
  const processor = ctx.createScriptProcessor(4096, 1, 1);

  processor.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);
    // Downsample (simple decimation with averaging) to targetSampleRate
    const outLength = Math.floor(input.length / ratio);
    const out = new Int16Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const start = Math.floor(i * ratio);
      const end = Math.min(input.length, Math.floor((i + 1) * ratio));
      let sum = 0;
      for (let j = start; j < end; j++) sum += input[j];
      const avg = sum / Math.max(1, end - start);
      const s = Math.max(-1, Math.min(1, avg));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    pushStream.write(out.buffer);
  };

  source.connect(processor);
  // Connect to a muted destination to keep the graph alive without playback
  const silentGain = ctx.createGain();
  silentGain.gain.value = 0;
  processor.connect(silentGain);
  silentGain.connect(ctx.destination);

  return {
    pushStream,
    cleanup: () => {
      try { processor.disconnect(); } catch { /* ignore */ }
      try { source.disconnect(); } catch { /* ignore */ }
      try { silentGain.disconnect(); } catch { /* ignore */ }
      try { pushStream.close(); } catch { /* ignore */ }
      ctx.close().catch(() => { /* ignore */ });
    },
  };
}

interface UseAzureSpeechRecordingOptions {
  onError?: (error: string) => void;
  languages?: string[];
  isCommandText?: (text: string) => boolean;
  skipTranslation?: boolean;
  /**
   * De AI-instellingen zijn hier weg: het vertalen loopt via /api/ai/translate en
   * de server leest endpoint, model en prompt zelf. Deze hook had ze alleen nodig om
   * ze aan translate-client door te geven.
   */
  onTranslationCost?: (inputCost: number) => void;
  /**
   * Optional MediaStream to transcribe instead of the default microphone.
   * When provided, audio is fed to Azure via a push stream (mono PCM 16k).
   */
  audioStream?: MediaStream | null;
}

export interface TranscriptMessage {
  id: string;
  speaker: string;
  text: string;
  translatedText: string;
  timestamp: Date;
  isPartial?: boolean;
  isCommand?: boolean;
  /** BCP-47 language detected by Azure for this utterance (e.g. 'nl-NL') */
  detectedLanguage?: string;
}

// Map Azure speaker IDs (Guest-1, Guest-2, etc.) to friendly labels
const speakerLabelMap = new Map<string, string>();
let speakerCounter = 0;

function getSpeakerLabel(speakerId: string | undefined): string {
  if (!speakerId || speakerId === 'Unknown' || speakerId === 'Unidentified') {
    return 'Spreker';
  }
  if (!speakerLabelMap.has(speakerId)) {
    speakerCounter += 1;
    speakerLabelMap.set(speakerId, `Spreker ${speakerCounter}`);
  }
  return speakerLabelMap.get(speakerId)!;
}

function resetSpeakerLabels() {
  speakerLabelMap.clear();
  speakerCounter = 0;
}

let messageIdCounter = 0;

export function useAzureSpeechRecording(options: UseAzureSpeechRecordingOptions = {}) {
  const { onError, languages = ['nl-NL'], isCommandText, skipTranslation = false, onTranslationCost, audioStream } = options;
  const audioStreamRef = useRef<MediaStream | null>(audioStream || null);
  useEffect(() => { audioStreamRef.current = audioStream || null; }, [audioStream]);
  const onTranslationCostRef = useRef(onTranslationCost);
  useEffect(() => { onTranslationCostRef.current = onTranslationCost; }, [onTranslationCost]);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [partialMessage, setPartialMessage] = useState<TranscriptMessage | null>(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const transcriberRef = useRef<SpeechSDK.ConversationTranscriber | null>(null);
  const translatePendingRef = useRef(0);
  const customAudioCleanupRef = useRef<(() => void) | null>(null);

  /**
   * Het laatste afgeronde bericht, buiten de React-state om bijgehouden.
   *
   * De `transcribed`-callback moet weten of dit fragment bij de vorige ballon hoort
   * of een nieuwe begint. Die vraag werd eerder binnen de `setMessages`-updater
   * beantwoord, waarna de uitkomst uit een variabele werd gelezen die de updater
   * had gezet. Dat leunt erop dat React de updater synchroon uitvoert, en dat doet
   * hij niet betrouwbaar: sinds React 18 worden updates uit een callback die niet
   * van React zelf komt -- zoals deze, die uit de Speech-websocket komt -- gebatcht.
   * React voert de updater dan pas bij de volgende render uit.
   *
   * Soms lukte het toch, omdat React de updater alvast uitvoert als er nog niets in
   * de wachtrij staat. Vandaar het grillige beeld: de ballon verscheen, maar de
   * melding "Kon transcriptiebericht niet verwerken" kwam erbij en de vertaling
   * bleef weg.
   *
   * Een ref wordt wél synchroon bijgewerkt, dus de beslissing staat vast voordat
   * `setMessages` wordt aangeroepen. Twee fragmenten die snel na elkaar
   * binnenkomen houden daarmee ook hun volgorde.
   */
  const laatsteBerichtRef = useRef<{ id: string; speaker: string; text: string } | null>(null);

  const translateToNl = useCallback(async (text: string) => {
    if (!text.trim()) return '';
    translatePendingRef.current += 1;
    setIsTranslating(true);
    try {
      const { vertaling, kosten } = await vertaal(text, 'Nederlands', 'soep');

      // De kosten komen van de server, met de marge er al op. De client rekende ze
      // tot fase 6 zelf uit, met tarieven die hij uit billing_settings had gelezen.
      if (kosten > 0) onTranslationCostRef.current?.(kosten);

      return vertaling;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Vertalen mislukt');
    } finally {
      translatePendingRef.current -= 1;
      if (translatePendingRef.current <= 0) {
        translatePendingRef.current = 0;
        setIsTranslating(false);
      }
    }
  }, []);

  // Compute flat transcript from messages for backward compatibility
  const fullTranscript = messages.map(m => {
    const label = m.speaker ? `${m.speaker}: ` : '';
    return `${label}${m.translatedText || m.text}`;
  }).join('\n').trim();

  const rawTranscript = messages.map(m => {
    const label = m.speaker ? `${m.speaker}: ` : '';
    return `${label}${m.text}`;
  }).join('\n').trim();

  const startRecording = useCallback(async () => {
    setIsConnecting(true);

    try {
      // Only request the default mic when we don't have a custom stream
      if (!audioStreamRef.current) {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      // De opname blijft in de browser -- de microfoon staat daar en continue
      // herkenning loopt over een websocket naar Azure. Wat verandert is dat dit
      // endpoint nu een sessie vereist: azure-speech-token was onbeschermd, dus elk
      // verzoek van internet liep op kosten van iSOFT.
      const { token, region } = await haalSpeechToken();

      const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
      
      speechConfig.setProperty(
        SpeechSDK.PropertyId.SpeechServiceConnection_LanguageIdMode,
        "Continuous"
      );

      const useLangs = languages.slice(0, 4);
      speechConfig.speechRecognitionLanguage = useLangs[0];

      let audioConfig: SpeechSDK.AudioConfig;
      if (audioStreamRef.current) {
        const built = buildPushStreamFromMediaStream(audioStreamRef.current);
        customAudioCleanupRef.current = built.cleanup;
        audioConfig = SpeechSDK.AudioConfig.fromStreamInput(built.pushStream);
      } else {
        audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      }

      let transcriber: SpeechSDK.ConversationTranscriber;

      if (useLangs.length > 1) {
        const autoDetectConfig = SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(useLangs);
        transcriber = SpeechSDK.ConversationTranscriber.FromConfig(speechConfig, autoDetectConfig, audioConfig);
      } else {
        transcriber = new SpeechSDK.ConversationTranscriber(speechConfig, audioConfig);
      }

      // Reset state
      setMessages([]);
      setPartialMessage(null);
      resetSpeakerLabels();
      messageIdCounter = 0;
      laatsteBerichtRef.current = null;

      transcriber.transcribing = (_s, e) => {
        const text = e.result.text || '';
        const speaker = getSpeakerLabel(e.result.speakerId);
        setPartialMessage({
          id: 'partial',
          speaker,
          text,
          translatedText: '',
          timestamp: new Date(),
          isPartial: true,
        });
      };

      transcriber.transcribed = async (_s, e) => {
        if (e.result.reason !== SpeechSDK.ResultReason.RecognizedSpeech) return;
        const text = e.result.text;
        if (!text || !text.trim()) return;

        const speaker = getSpeakerLabel(e.result.speakerId);
        let detectedLanguage: string | undefined;
        try {
          detectedLanguage = SpeechSDK.AutoDetectSourceLanguageResult.fromConversationTranscriptionResult(e.result)?.language;
        } catch {
          detectedLanguage = undefined;
        }
        if (!detectedLanguage) {
          detectedLanguage = e.result.properties?.getProperty('SpeechServiceConnection_AutoDetectSourceLanguageResult') || undefined;
        }

        const forceNewBalloon = isCommandText ? isCommandText(text) : false;

        // Aansluiten bij de vorige ballon van dezelfde spreker, of een nieuwe
        // beginnen. Een commandotekst krijgt altijd een eigen ballon. Deze
        // beslissing valt hier, vóór setMessages -- zie laatsteBerichtRef.
        const vorige = laatsteBerichtRef.current;
        const aansluiten = !forceNewBalloon && vorige !== null && vorige.speaker === speaker;

        let currentMsgId: string;
        let currentText: string;

        if (aansluiten) {
          currentMsgId = vorige.id;
          currentText = `${vorige.text} ${text}`;
        } else {
          messageIdCounter += 1;
          currentMsgId = `msg-${messageIdCounter}`;
          currentText = text;
        }

        // Een commandoballon is afgesloten: het volgende fragment hoort er niet bij.
        laatsteBerichtRef.current = forceNewBalloon
          ? null
          : { id: currentMsgId, speaker, text: currentText };

        setMessages(prev => {
          const last = prev.length > 0 ? prev[prev.length - 1] : null;

          if (aansluiten && last && last.id === currentMsgId) {
            const updated: TranscriptMessage = {
              ...last,
              detectedLanguage: detectedLanguage || last.detectedLanguage,
              text: currentText,
              translatedText: '', // wordt hieronder opnieuw vertaald
            };
            return [...prev.slice(0, -1), updated];
          }

          const newMsg: TranscriptMessage = {
            id: currentMsgId,
            speaker,
            text: currentText,
            translatedText: '',
            timestamp: new Date(),
            detectedLanguage,
            isCommand: forceNewBalloon,
          };
          return [...prev, newMsg];
        });
        setPartialMessage(null);

        // Skip translation for command messages (trigger phrases) and when languages match
        if (skipTranslation || forceNewBalloon) {
          // No translation needed — copy text as translatedText
          setMessages(prev => prev.map(m =>
            m.id === currentMsgId ? { ...m, translatedText: m.text } : m
          ));
        } else {
          // De vertaling stond hiervoor binnen een setMessages-updater, die `prev`
          // ongewijzigd teruggaf en alleen als plek diende om translateToNl aan te
          // roepen. Een updater hoort puur te zijn: React mag hem overslaan of
          // meer dan eens uitvoeren, en elke uitvoering was hier een extra
          // vertaalaanroep -- die per stuk wordt doorbelast.
          try {
            const nl = await translateToNl(currentText);
            setMessages(prev => prev.map(m =>
              m.id === currentMsgId ? { ...m, translatedText: nl } : m
            ));
          } catch (err) {
            // Zonder vertaling is de brontekst bruikbaarder dan een lege ballon.
            setMessages(prev => prev.map(m =>
              m.id === currentMsgId ? { ...m, translatedText: currentText } : m
            ));
            const msg = err instanceof Error ? err.message : 'Vertalen mislukt';
            onErrorRef.current?.(msg);
          }
        }
      };

      transcriber.canceled = (_s, e) => {
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          console.error('Azure Speech canceled:', e.errorDetails);
          onErrorRef.current?.(`Spraakherkenning fout: ${e.errorDetails}`);
        }
      };

      transcriber.sessionStopped = () => {
        // Only set isRecording to false if we're not just pausing
        // When pausing, isPaused will be set separately
      };

      transcriberRef.current = transcriber;
      
      transcriber.startTranscribingAsync(
        () => {
          setIsRecording(true);
          setIsConnecting(false);
        },
        (err) => {
          console.error('Failed to start Azure transcription:', err);
          onErrorRef.current?.('Kon spraakherkenning niet starten');
          setIsConnecting(false);
        }
      );

      return true;
    } catch (error) {
      console.error('Failed to start Azure recording:', error);
      const message = error instanceof Error ? error.message : 'Kon opname niet starten';
      onErrorRef.current?.(message);
      setIsConnecting(false);
      return false;
    }
  }, [translateToNl, languages]);

  const stopRecording = useCallback(() => {
    if (transcriberRef.current) {
      transcriberRef.current.stopTranscribingAsync(
        () => {
          transcriberRef.current?.close();
          transcriberRef.current = null;
        },
        (err) => {
          console.error('Error stopping transcription:', err);
          transcriberRef.current?.close();
          transcriberRef.current = null;
        }
      );
    }
    if (customAudioCleanupRef.current) {
      try { customAudioCleanupRef.current(); } catch { /* ignore */ }
      customAudioCleanupRef.current = null;
    }
    setIsRecording(false);
    setIsPaused(false);
  }, []);

  const pauseRecording = useCallback(() => {
    if (transcriberRef.current && isRecording && !isPaused) {
      transcriberRef.current.stopTranscribingAsync(
        () => {
          setIsPaused(true);
        },
        (err) => {
          console.error('Error pausing transcription:', err);
        }
      );
    }
  }, [isRecording, isPaused]);

  const resumeRecording = useCallback(() => {
    if (transcriberRef.current && isRecording && isPaused) {
      transcriberRef.current.startTranscribingAsync(
        () => {
          setIsPaused(false);
        },
        (err) => {
          console.error('Error resuming transcription:', err);
          onErrorRef.current?.('Kon opname niet hervatten');
        }
      );
    }
  }, [isRecording, isPaused]);

  const clearTranscript = useCallback(() => {
    setMessages([]);
    setPartialMessage(null);
    resetSpeakerLabels();
    messageIdCounter = 0;
    laatsteBerichtRef.current = null;
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<TranscriptMessage>) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (transcriberRef.current) {
        try {
          transcriberRef.current.stopTranscribingAsync(() => {
            transcriberRef.current?.close();
          });
        } catch {
          // ignore
        }
      }
    };
  }, []);

  return {
    isRecording,
    isPaused,
    isConnecting,
    isTranslating,
    isSupported: true,
    isReady: true,
    rawTranscript,
    fullTranscript,
    messages,
    partialMessage,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearTranscript,
    updateMessage,
  };
}
